import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Home, BarChart3, UserCog, FileText, Bell, ChevronDown, Users, CheckCircle, XCircle, AlertTriangle, UploadCloud, BookOpen, Map, ArrowLeft, Loader2 } from 'lucide-react';
// Mock Data
const importedLearners = [{
  id: 1,
  name: 'Sarah Johnson',
  email: 'sarah.j@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 2,
  name: 'Michael Chen',
  email: 'mchen@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 3,
  name: 'Emily Davis',
  email: 'emily.d@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 4,
  name: 'John Smith',
  email: 'john.smith@example.com',
  status: 'warning',
  message: 'Account already exists, will update'
}, {
  id: 5,
  name: 'Lisa Wong',
  email: 'lisa.w@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 6,
  name: 'Alex Rivera',
  email: 'alex.r@example.com',
  status: 'success',
  message: 'Ready to import'
}, {
  id: 7,
  name: 'David Kim',
  email: 'david.k@example.com',
  status: 'error',
  message: 'Invalid email format'
}, {
  id: 8,
  name: 'Rachel Green',
  email: 'rachel.g@example.com',
  status: 'success',
  message: 'Ready to import'
}];
const availableSkills = ['AI Functionality Beyond the Hype', 'Active and Empathetic Listening', 'DEI: Employee Relations', 'Developing EQ and Empathy', 'EQ for the Workplace', 'Effective Communication'];
const availablePathways = ['Leadership Essentials', 'Technical Foundations', 'Communication Mastery'];
export function BulkUploadResultsPage() {
  const navigate = useNavigate();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  // Assignment State
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedPathways, setSelectedPathways] = useState<string[]>([]);
  const [enableAllSkills, setEnableAllSkills] = useState(false);
  const [enableAllPathways, setEnableAllPathways] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);
  // Sync "Enable All" toggles with individual selections
  useEffect(() => {
    if (enableAllSkills) {
      setSelectedSkills(availableSkills);
    } else if (selectedSkills.length === availableSkills.length) {
      setSelectedSkills([]);
    }
  }, [enableAllSkills]);
  useEffect(() => {
    if (selectedSkills.length === availableSkills.length && availableSkills.length > 0) {
      setEnableAllSkills(true);
    } else if (selectedSkills.length < availableSkills.length) {
      setEnableAllSkills(false);
    }
  }, [selectedSkills]);
  useEffect(() => {
    if (enableAllPathways) {
      setSelectedPathways(availablePathways);
    } else if (selectedPathways.length === availablePathways.length) {
      setSelectedPathways([]);
    }
  }, [enableAllPathways]);
  useEffect(() => {
    if (selectedPathways.length === availablePathways.length && availablePathways.length > 0) {
      setEnableAllPathways(true);
    } else if (selectedPathways.length < availablePathways.length) {
      setEnableAllPathways(false);
    }
  }, [selectedPathways]);
  const toggleSkill = (skill: string) => {
    setSelectedSkills(prev => prev.includes(skill) ? prev.filter(s => s !== skill) : [...prev, skill]);
  };
  const togglePathway = (pathway: string) => {
    setSelectedPathways(prev => prev.includes(pathway) ? prev.filter(p => p !== pathway) : [...prev, pathway]);
  };
  const handleConfirm = () => {
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/assessment'); // Navigate back to users page
    }, 1500);
  };
  const stats = [{
    label: 'Total Processed',
    value: '124',
    icon: Users,
    color: 'text-[#30718D]',
    bg: 'bg-[#30718D]/10'
  }, {
    label: 'Ready to Import',
    value: '120',
    icon: CheckCircle,
    color: 'text-[#007558]',
    bg: 'bg-[#007558]/10'
  }, {
    label: 'Needs Attention',
    value: '4',
    icon: AlertTriangle,
    color: 'text-yellow-600',
    bg: 'bg-yellow-100'
  }];
  return <div className="flex-1 bg-gray-50 min-h-screen pb-12" data-id="element-2186" data-mp="1kqhoex0r6c89">
      {/* Top Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30" data-id="element-2187" data-mp="1ksu5z92lmkks">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2188" data-mp="1yqir8i0kvtqd">
          <div className="flex items-center h-16" data-id="element-2189" data-mp="1ts7f20mt974z">
            {/* Logo */}
            <div className="text-[#DB3725] font-bold text-xl" data-id="element-2190" data-mp="2dcg4hzn4hlj5">LOGO</div>

            {/* Navigation Links */}
            <nav className="flex items-center gap-4 sm:gap-8 ml-6 sm:ml-12" data-id="element-2191" data-mp="2icavs4at8d4q">
              <Link data-phoebe-nav="/admin" to="/admin" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2192" data-mp="24cjk4a7rn8tw">
                <Home size={18} data-id="element-2193" data-mp="28fy1p5bcrx9g" />
                <span className="hidden sm:inline" data-id="element-2194" data-mp="1umq9bbfg6xqp">Admin Hub</span>
              </Link>
              <Link data-phoebe-nav="/analytics" to="/analytics" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2195" data-mp="1zd6op12twtfp">
                <BarChart3 size={18} data-id="element-2196" data-mp="1km7hnuhn6cy8" />
                <span className="hidden sm:inline" data-id="element-2197" data-mp="1pmu5bc6r36b5">Analytics</span>
              </Link>
              <Link data-phoebe-nav="/feedback" to="/feedback" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-2198" data-mp="1bnm64jal7y6y">
                <FileText size={18} data-id="element-2199" data-mp="1gorwiagrz9db" />
                <span className="hidden sm:inline" data-id="element-2200" data-mp="3icebfadjq0bs">Reports</span>
              </Link>
              <Link data-phoebe-nav="/assessment" to="/assessment" className="flex items-center gap-2 text-[#30718D] border-b-2 border-[#30718D] pb-[22px] pt-[22px] transition-colors text-sm font-medium" data-id="element-2201" data-mp="3dl4pajr7hf4n">
                <UserCog size={18} data-id="element-2202" data-mp="34kq2ffylfrz1" />
                <span className="hidden sm:inline" data-id="element-2203" data-mp="34lrbxfzyo6en">Users</span>
              </Link>
            </nav>

            {/* Right Side - User Info */}
            <div className="flex items-center gap-2 sm:gap-4 ml-auto" data-id="element-2204" data-mp="52yrltyxxxbl">
              <Link to="/alerts" className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors" data-id="element-2205" data-mp="71b9nnogqyt">
                <Bell size={20} data-id="element-2206" data-mp="3sc57vhlo2pei" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-[#DB3725] rounded-full" data-id="element-2207" data-mp="3nb7nqzh3jlad"></span>
              </Link>
              <div className="relative" ref={userMenuRef} data-id="element-2208" data-mp="2gv5esjrhw6jf">
                <button onClick={() => setIsUserMenuOpen(!isUserMenuOpen)} className="flex items-center gap-3 hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-2209" data-mp="2gsj0m1a3mss8">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 text-sm font-medium" data-id="element-2210" data-mp="rfb5otzxwf9y">
                    LT
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-gray-900" data-id="element-2211" data-mp="10dmwox4pxf2x">
                    Linda Taylor
                  </span>
                  <ChevronDown size={16} className="text-gray-600" data-id="element-2212" data-mp="156zh0v2d6zpv" />
                </button>

                {isUserMenuOpen && <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50" data-id="element-2213" data-mp="1f32jy351j2dv">
                    <div className="py-2" data-id="element-2214" data-mp="1k3gw9xdocyhp">
                      <Link to="/profile" className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm" data-id="element-2215" data-mp="1k0kw02ectxq3">
                        <Users size={18} className="text-gray-600" data-id="element-2216" data-mp="3lutyufs66ual" />
                        <span className="font-medium text-gray-900" data-id="element-2217" data-mp="3pv423kokv6lr">
                          Profile
                        </span>
                      </Link>
                      <div className="border-t border-gray-200 my-2" data-id="element-2218" data-mp="36xap1d6041q2"></div>
                      <button className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors text-sm text-[#DB3725]" data-id="element-2219" data-mp="31z93mll3rqbf">
                        <span className="font-medium" data-id="element-2220" data-mp="3bpu30b8loyve">Logout</span>
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" data-id="element-2221" data-mp="2j2hmoit19naj">
        {/* Page Header */}
        <div data-phoebe-tour="bulk-upload" className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-2222" data-mp="2x2ioc0h4gyr2">
          <div data-id="element-2223" data-mp="2s47dpnj06k7s">
            <Link to="/assessment" className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-2" data-id="element-2224" data-mp="cagjqo4f5yar">
              <ArrowLeft size={16} data-id="element-2225" data-mp="7bdplypz9chk" />
              Back to Users
            </Link>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-3" data-id="element-2226" data-mp="fruvbmu7e1qs">
              <UploadCloud className="text-[#30718D]" size={32} data-id="element-2227" data-mp="jv0w4eyjr0pv" />
              Upload Results
            </h1>
            <p className="text-gray-600 mt-1" data-id="element-2228" data-mp="5w19tdyiaxwv">
              Review your imported learners and assign their initial learning
              paths.
            </p>
          </div>
          <div className="flex items-center gap-3 self-start md:self-auto" data-id="element-2229" data-mp="yk6w4557kuyt">
            <button onClick={() => navigate('/assessment')} className="px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium text-sm" data-id="element-2230" data-mp="13iqi25jsy6r6">
              Cancel
            </button>
            <button onClick={handleConfirm} disabled={isSubmitting} className="flex items-center gap-2 px-6 py-2.5 bg-[#DB3725] text-white rounded-lg hover:bg-[#c02f1f] transition-colors font-medium text-sm shadow-sm disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-2231" data-mp="tr48ser0yhr2">
              {isSubmitting ? <>
                  <Loader2 size={18} className="animate-spin" data-id="element-2232" data-mp="2j7fwq98yms8s" />
                  Processing...
                </> : <>
                  <CheckCircle size={18} data-id="element-2233" data-mp="2o3wvryhp6r0n" />
                  Confirm & Import
                </>}
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-8" data-id="element-2234" data-mp="2f401nhcjgzy7">
          {stats.map((stat, index) => {
          const Icon = stat.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-2235" data-mp="256vepfavd0gc" data-mpi={index}>
                <div className={`w-12 h-12 rounded-full ${stat.bg} flex items-center justify-center flex-shrink-0`} data-id="element-2236" data-mp="1hhatepr8tr3w">
                  <Icon size={24} className={stat.color} data-id="element-2237" data-mp="1mfcdne0enwgy" />
                </div>
                <div data-id="element-2238" data-mp="12u2f3g2n15np">
                  <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-2239" data-mp="12vdru5e29nqy">
                    {stat.label}
                  </div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2240" data-mp="xy59o4swxjpp">
                    {stat.value}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2241" data-mp="1lt7ve3wf771y">
          {/* Left Column: Imported Learners Table */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col h-[600px]" data-id="element-2242" data-mp="1gtlmg6nmkcyy">
            <div className="p-5 border-b border-gray-200 bg-gray-50/50 flex items-center justify-between" data-id="element-2243" data-mp="24l0ri76sd8dw">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2244" data-mp="20k74rh52xzml">
                Import Preview
              </h2>
              <span className="text-sm text-gray-500" data-id="element-2245" data-mp="27nn9nji2s4vw">
                Showing {importedLearners.length} of 124
              </span>
            </div>

            <div className="flex-1 overflow-auto" data-id="element-2246" data-mp="2hjqdctsksbky">
              <table className="w-full text-left border-collapse" data-id="element-2247" data-mp="2hhx6pehyprcy">
                <thead className="bg-white sticky top-0 z-10 shadow-sm" data-id="element-2248" data-mp="2mbi7tfeur1gd">
                  <tr data-id="element-2249" data-mp="2qctqf0zb6mf2">
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-2250" data-mp="2qbiiqq2qsuio">
                      Learner
                    </th>
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 hidden sm:table-cell" data-id="element-2251" data-mp="30bj6co7n9wso">
                      Email
                    </th>
                    <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-2252" data-mp="15xs100wclz31">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-2253" data-mp="3o7xn554om3hk">
                  {importedLearners.map((learner, index) => <motion.tr key={learner.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.4 + index * 0.05
                }} className="hover:bg-gray-50 transition-colors" data-id="element-2254" data-mp="3jd08niomv1xa" data-mpi={index}>
                      <td className="py-3 px-5" data-id="element-2255" data-mp="6xg2x4kfbidt">
                        <div className="flex items-center gap-3" data-id="element-2256" data-mp="6vlgtmxf1k85">
                          <div className="w-8 h-8 rounded-full bg-[#30718D]/10 flex items-center justify-center text-[#30718D] text-xs font-bold flex-shrink-0" data-id="element-2257" data-mp="1xjumt4p14sg">
                            {learner.name.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div data-id="element-2258" data-mp="2hp7twv76j9ef">
                            <div className="font-medium text-gray-900 text-sm" data-id="element-2259" data-mp="2hozh13b24438">
                              {learner.name}
                            </div>
                            <div className="text-xs text-gray-500 sm:hidden" data-id="element-2260" data-mp="e7j4sedyv1zj">
                              {learner.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-5 text-sm text-gray-600 hidden sm:table-cell" data-id="element-2261" data-mp="5fzac4gd3ead">
                        {learner.email}
                      </td>
                      <td className="py-3 px-5" data-id="element-2262" data-mp="x75f4y71205z">
                        {learner.status === 'success' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#007558]/10 text-[#007558] text-xs font-medium" data-id="element-2263" data-mp="j6uljnec27h4">
                            <CheckCircle size={14} data-id="element-2264" data-mp="o4nv8qeuhjm5" />
                            <span data-id="element-2265" data-mp="33xczcf7r7ehe">Ready</span>
                          </div>}
                        {learner.status === 'warning' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium" title={learner.message} data-id="element-2266" data-mp="38wpl0wmw2fie">
                            <AlertTriangle size={14} data-id="element-2267" data-mp="2wdkiy0b4ybt3" />
                            <span data-id="element-2268" data-mp="2wcsm6m5sy818">Update</span>
                          </div>}
                        {learner.status === 'error' && <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium" title={learner.message} data-id="element-2269" data-mp="3actom61ptnit">
                            <XCircle size={14} data-id="element-2270" data-mp="2hnwdcmvx8qzk" />
                            <span data-id="element-2271" data-mp="2hml9yo6e1l8z">Error</span>
                          </div>}
                      </td>
                    </motion.tr>)}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-gray-200 bg-gray-50 text-center" data-id="element-2272" data-mp="2rgkaxjuq6yzc">
              <button className="text-sm font-medium text-[#30718D] hover:text-[#265a70] transition-colors" data-id="element-2273" data-mp="2mm7t1e8wdvpp">
                View all 124 records
              </button>
            </div>
          </motion.div>

          {/* Right Column: Assignment Controls */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.4
        }} className="space-y-6" data-id="element-2274" data-mp="3u1utvsfhpw49">
            {/* Pathways Assignment */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2275" data-mp="7nv23pr41ti5">
              <div className="p-5 border-b border-gray-200 bg-gray-50/50" data-id="element-2276" data-mp="cmxv2bttg99r">
                <div className="flex items-center gap-2 mb-1" data-id="element-2277" data-mp="3b3sabt5pordu">
                  <Map size={18} className="text-[#30718D]" data-id="element-2278" data-mp="3g120xc4u7939" />
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-2279" data-mp="3g3o83antfmky">
                    Assign Pathways
                  </h2>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2280" data-mp="3p2i9rfg0735d">
                  Select learning pathways for all imported users.
                </p>
              </div>

              <div className="p-5" data-id="element-2281" data-mp="10cs4skiu9hps">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-2282" data-mp="14b9f1cxk38bf">
                  <label htmlFor="toggle-all-pathways" className="text-sm font-semibold text-gray-900 cursor-pointer" data-id="element-2283" data-mp="2ot0fhmlku5y1">
                    Enable All Pathways
                  </label>
                  <button id="toggle-all-pathways" role="switch" aria-checked={enableAllPathways} onClick={() => setEnableAllPathways(!enableAllPathways)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${enableAllPathways ? 'bg-[#30718D]' : 'bg-gray-200'}`} data-id="element-2284" data-mp="2g186zhg456w3">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableAllPathways ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2285" data-mp="216tve1yvozrg" />
                  </button>
                </div>

                <div className="space-y-3" data-id="element-2286" data-mp="3rhgfdzi87cfo">
                  {availablePathways.map(pathway => <label key={pathway} className="flex items-start gap-3 cursor-pointer group" data-id="element-2287" data-mp="3mhu2xsa8207x">
                      <div className="flex items-center h-5 mt-0.5" data-id="element-2288" data-mp="2vchoh070loz9">
                        <input type="checkbox" checked={selectedPathways.includes(pathway)} onChange={() => togglePathway(pathway)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] transition-colors" data-id="element-2289" data-mp="2vd9k2b0m3mpj" />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-2290" data-mp="2qkznic4zroea">
                        {pathway}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>

            {/* Skills Assignment */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-2291" data-mp="22oc083gr5huo">
              <div className="p-5 border-b border-gray-200 bg-gray-50/50" data-id="element-2292" data-mp="27mdl8txqnr8s">
                <div className="flex items-center gap-2 mb-1" data-id="element-2293" data-mp="wfr10i24k32">
                  <BookOpen size={18} className="text-[#30718D]" data-id="element-2294" data-mp="wz2pzh46c8n" />
                  <h2 className="text-lg font-bold text-gray-900" data-id="element-2295" data-mp="5vsffpoktz7r">
                    Assign Skills
                  </h2>
                </div>
                <p className="text-xs text-gray-500" data-id="element-2296" data-mp="1qbgq9gfcx5q6">
                  Select individual skills for all imported users.
                </p>
              </div>

              <div className="p-5" data-id="element-2297" data-mp="204fozr4kicni">
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-2298" data-mp="205h1m018f77c">
                  <label htmlFor="toggle-all-skills" className="text-sm font-semibold text-gray-900 cursor-pointer" data-id="element-2299" data-mp="1val10bhxjbim">
                    Enable All Skills
                  </label>
                  <button id="toggle-all-skills" role="switch" aria-checked={enableAllSkills} onClick={() => setEnableAllSkills(!enableAllSkills)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#30718D] focus:ring-offset-2 ${enableAllSkills ? 'bg-[#30718D]' : 'bg-gray-200'}`} data-id="element-2300" data-mp="244pvr606p73f">
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enableAllSkills ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-2301" data-mp="jpwb1aj70iri" />
                  </button>
                </div>

                <div className="space-y-3 max-h-[240px] overflow-y-auto pr-2" data-id="element-2302" data-mp="27ulff2ujyqso">
                  {availableSkills.map(skill => <label key={skill} className="flex items-start gap-3 cursor-pointer group" data-id="element-2303" data-mp="2mjq14u0k3d5j">
                      <div className="flex items-center h-5 mt-0.5" data-id="element-2304" data-mp="2hloi2934rdu2">
                        <input type="checkbox" checked={selectedSkills.includes(skill)} onChange={() => toggleSkill(skill)} className="w-4 h-4 rounded border-gray-300 text-[#30718D] focus:ring-[#30718D] transition-colors" data-id="element-2305" data-mp="1tusigpsfnec9" />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-2306" data-mp="23rotp39pueki">
                        {skill}
                      </span>
                    </label>)}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}